// =============================================================================
// usePageDirtyGuard — "this page holds unsaved typed work", for the app-wide
// leave guard (src/lib/pageDirty.ts). The page-level sibling of useDirtyClose,
// which does the same job for one modal.
//
// It also installs the beforeunload warning, so a page adopting the guard
// covers all three exits at once: the in-app back arrow / nav (via the
// registry), the browser tab close and the hard reload. "Nav" here is the
// back arrow, the bottom bar, the desktop rail and the ⌘K palette.
//
//   const snapshot = view === 'capture' ? JSON.stringify(form) : null
//   const { dirty, markSaved } = usePageDirtyGuard(snapshot)
//   …
//   onSuccess: () => { markSaved(); … }
//
// -- The arming rules, which are the whole point ------------------------------
//
// Nobody should ever see "Discard unsaved changes?" on a form they have not
// touched. Every hand-rolled version of this guard in the repo got that wrong
// in the same way and then patched around it differently (ExporterConfig's
// `seenLoaded` ref, ExporterShipmentDetail's `seededRef`, ExporterInvoices'
// baseline re-armed on view change, Scouting's comment at :2772): a page seeds
// its form from a query AFTER mount, so a baseline captured at mount is the
// EMPTY form and the very first hydration reads as an edit.
//
// So the baseline is not captured at mount. It is armed by the snapshot itself:
//
//   • `null`      → the page is not guarding anything yet (still loading, or
//                   the capture surface is not on screen). Never dirty, and any
//                   previously armed baseline is dropped.
//   • non-null    → if no baseline is armed, this value BECOMES the baseline,
//                   in the same render it arrives. There is no frame in which a
//                   freshly hydrated form is considered dirty.
//   • `rearmKey`  → changing it drops the baseline, so opening a second record
//                   in the same mounted page re-arms against the new record.
//
// Two things a page must still do itself:
//   1. Pass `null` until EVERY field is seeded. A form that hydrates in two
//      steps will read the second step as an edit.
//   2. Seed once — the `if (form === null && query.data) seed(…)` idiom — so a
//      background refetch cannot overwrite typed values (which would look like
//      the operator's own edit, and, worse, silently lose their work).
// =============================================================================

import { useCallback, useEffect, useId, useRef } from 'react'
import { registerPageDirty, type LeavePrompt, type LeaveScope } from '@/lib/pageDirty'
import { useUnloadWarning } from '@/hooks/useUnloadWarning'

export interface PageDirtyGuard {
  /** True only when a baseline is armed AND the snapshot has moved off it. */
  dirty: boolean
  /** Re-arm the baseline to the current snapshot — call after a successful
   *  save, or the page keeps warning about work that is already on the server. */
  markSaved: () => void
}

export function usePageDirtyGuard(snapshot: string | null, rearmKey?: string): PageDirtyGuard {
  const id = useId()
  const baseline = useRef<string | null>(null)
  const armedFor = useRef<string | undefined>(rearmKey)
  const latest = useRef<string | null>(snapshot)
  const dirtyRef = useRef(false)

  latest.current = snapshot

  // Arm during RENDER, not in an effect. An effect runs a frame late, and for
  // that frame the freshly-seeded form would compare against a null baseline —
  // exactly the false positive this hook exists to prevent. Assigning a ref
  // here is idempotent (a second render with the same inputs computes the same
  // baseline), so StrictMode's double render is harmless.
  if (armedFor.current !== rearmKey) { armedFor.current = rearmKey; baseline.current = null }
  if (snapshot === null) baseline.current = null
  else if (baseline.current === null) baseline.current = snapshot

  const dirty = snapshot !== null && baseline.current !== null && snapshot !== baseline.current
  dirtyRef.current = dirty

  // Register a PREDICATE, not the value: the registry is read at click time, so
  // the entry never needs re-registering as the page is typed into.
  useEffect(() => registerPageDirty(id, () => dirtyRef.current), [id])

  // Tab close / hard reload — the one exit no in-app control can intercept.
  useUnloadWarning(dirty)

  const markSaved = useCallback(() => { baseline.current = latest.current }, [])

  return { dirty, markSaved }
}

// A recording lives in the mounted page: a link to the same path with another
// query (the Farm Map's ?block=, a URL filter) does not stop it, so it must not
// ask (pageDirty LeaveScope, M-01 fix round 2).
const RECORDING_SCOPE: LeaveScope = { samePathStays: true }

/**
 * The same guard for a page whose unsaved thing is a live RECORDING rather
 * than typed work — Scouting's GPS walk. Leaving the page stops the recording
 * (the unmount is deliberate: a walk must never run on in the background), so
 * the bottom nav, the back arrow and ⌘K must ask first, with the page's own
 * question. There is no baseline to arm: the page is "dirty" exactly while
 * `active` is true (farm audit SC-07 — the Map/Trends/Report links asked; the
 * app's own navigation did not).
 *
 * It does NOT install a beforeunload warning: a recording page decides that
 * itself, because it also has to snapshot what it recorded before the tab goes.
 */
export function usePageLeaveGuard(active: boolean, prompt: LeavePrompt): void {
  const id = useId()
  const activeRef = useRef(active)
  activeRef.current = active
  // Pass a module-level constant: a new object per render re-registers.
  useEffect(() => registerPageDirty(id, () => activeRef.current, prompt, RECORDING_SCOPE), [id, prompt])
}
